/** Export current review values separately from historical processing evidence. */
import { applyGtConfigEdits } from '../reviewEdits';
import { assignmentOutcomeLabel } from './gtAssignmentOutcomes';

const cell = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : v);
const fingerprint = (row) => JSON.stringify([
  (row.audiences || []).map((a) => [a.type, String(a.id), a.exclude === true]),
  row.location_group, row.neighborhoods, row.proximity_mode, row.radial,
  row.drive_to_location_group, row.tracking_visits_to, row.drive_to_type_name, row.drive_to_type,
  row.tracking_visits_to_array,
]);
const roleFor = (type) => ({ custom_audience: 'custom', behavior: 'behavior',
  segment: 'third_party', brand: 'brand', location_group: 'location' }[type] || type);
const roleType = { custom: 'custom_audience', behavior: 'behavior', third_party: 'segment', location_brand: 'brand' };
const identities = (items) => JSON.stringify(items.map((a) =>
  [a.type || 'location_group', String(a.id), a.exclude === true]).sort());
const measurementPick = (row) => {
  const pick = row.drive_to_location_group;
  const target = row.tracking_visits_to || row.drive_to_type_name;
  const normalized = (v) => String(v || '').trim().toLowerCase();
  return pick?.id && pick.type === 'location_group' && normalized(target) === normalized(pick.name)
    && normalized(row.drive_to_type) !== 'brand' ? pick : null;
};

function evidenceStatus(report, base, current, index) {
  const row = current.adgroups[index] || {};
  const edited = 'Historical evidence; selections edited in review (not re-matched)';
  if (fingerprint(base.adgroups[index] || {}) !== fingerprint(row)) return edited;
  const snapshots = (report.operations || []).flatMap((o) => o.assignment_outcomes || [])
    .filter((a) => a.row?.index === index && Array.isArray(a.selections));
  if (!snapshots.length) return 'Historical processing evidence; current selection comparison unavailable';
  for (const snapshot of snapshots) {
    const type = roleType[snapshot.role];
    const group = snapshot.role === 'drive_to_measurement' ? measurementPick(row) : row.location_group;
    const picks = type ? (row.audiences || []).filter((a) => a.type === type && String(a.id) !== '49124')
      : group?.id && String(group.id) !== '92006' ? [group] : [];
    const placeholder = type ? snapshot.role !== 'location_brand'
      && (row.audiences || []).some((a) => String(a.id) === '49124') : String(group?.id) === '92006';
    if (identities(snapshot.selections) !== identities(picks) || snapshot.placeholder_present !== placeholder
      || (snapshot.row.ad_group_name && snapshot.row.ad_group_name !== row.ad_group_name)
      || (snapshot.role === 'on_premise' && (snapshot.proximity_mode !== row.proximity_mode
        || snapshot.radial !== row.radial))) return edited;
  }
  return 'Processing snapshot; current selections unchanged';
}

/** AOA sheets, pure and testable without a browser or Excel dependency.
 * Row IDs and list edits use the same writer as Save/Approve; filters apply to
 * all row-scoped evidence. Instructions/cache metadata remain campaign context.
 */
export function buildGtReviewAuditSheets({ ctsReview, visibleRows, editedRows = {},
  pendingListOps = {}, pendingDeletions = {} }) {
  const base = ctsReview?.result;
  if (!Array.isArray(base?.adgroups)) return [];
  const lists = Object.keys(pendingListOps || {}).length ? pendingListOps : pendingDeletions;
  const current = applyGtConfigEdits(base, editedRows, [], lists);
  const report = ctsReview.instruction_enrichment || base._instruction_enrichment || {};
  const visible = new Set((visibleRows || []).map((r) => /^gt-ag-(\d+)$/.exec(r.rowId)?.[1])
    .filter((i) => i != null).map(Number));
  const targeting = [['Row', 'Ad group', 'Package name', 'Role', 'Type', 'ID', 'Name',
    'Excluded', 'Placeholder', 'Mode', 'Radius (miles)', 'Processing evidence']];
  const evidence = [['Operation', 'Source', 'Row', 'Ad group', 'Role', 'Processing outcome',
    'Query', 'Evidence source', 'Description source', 'Resolved type', 'Resolved ID',
    'Resolved name', 'Score', 'Threshold', 'Margin', 'Required margin', 'Reason',
    'Candidates (not assignments)', 'Rejected candidates', 'Attempts', 'Evidence status']];
  const state = (i) => evidenceStatus(report, base, current, i);
  for (const i of visible) {
    const row = current.adgroups[i];
    if (!row) continue;
    const add = (resource, role, type) => targeting.push([i + 1, row.ad_group_name,
      row.package_name || '', role, type || resource?.type || '', String(resource?.id ?? ''),
      resource?.name || '', resource?.exclude === true, ['49124', '92006'].includes(String(resource?.id)),
      row.proximity_mode || '', row.radial ?? '', state(i)]);
    for (const audience of row.audiences || []) add(audience, roleFor(audience.type));
    // An empty array is explicit, rather than disappearing from the workbook.
    if (!(row.audiences || []).length) add(null, 'audiences: none');
    const locationRole = Object.keys(row._targeting_selection || {})
      .find((r) => ['on_premise', 'location', 'competitor_location', 'neighborhood'].includes(r)) || 'location';
    if (row.location_group?.id) add(row.location_group, locationRole, 'location_group');
    for (const group of row.neighborhoods || []) {
      if (String(group.id) !== String(row.location_group?.id)) add(group, 'neighborhood', 'location_group');
    }
    const driveTo = measurementPick(row);
    if (driveTo) add(driveTo, 'drive_to_measurement', driveTo.type);
    else if (row.tracking_visits_to || row.drive_to_type_name) {
      add({ name: row.tracking_visits_to || row.drive_to_type_name }, 'drive_to_measurement',
        row.drive_to_type === 'Brand' ? 'brand' : 'location_group');
    }
  }
  for (const operation of report.operations || []) {
    for (const e of operation.targeting_resolution || []) {
      const i = e.row?.index;
      if (!visible.has(i)) continue;
      const outcome = operation.assignment_outcomes?.find((a) => a.row?.index === i && a.role === e.role);
      evidence.push([operation.id, operation.source, i + 1, current.adgroups[i]?.ad_group_name,
        e.role, assignmentOutcomeLabel(outcome?.outcome || e.assignment_outcome), e.query,
        e.evidence_source, e.description_source, e.resolved?.type, e.resolved?.id,
        e.resolved?.name, e.score, e.threshold, e.margin, e.required_margin, e.reason,
        cell(e.candidates), cell(e.rejected_candidates), cell(e.attempts), state(i)].map(cell));
    }
  }
  const instructions = [['Kind', 'Source / key', 'ID', 'Text / value'],
    ['Scope', 'visible ad groups', '', [...visible].map((i) => i + 1).join(', ')],
    ['Version', 'processor', '', report.processor_version || report.version || ''],
    ['Instructions', 'additional_instructions', '', report.instruction_snapshot?.additional_instructions || ''],
    ['Instructions', 'am_notes', '', report.instruction_snapshot?.am_notes || ''],
    ['Cache snapshot', 'metadata', '', cell(report.cache_snapshot || {})],
    ...(report.instructions || []).map((it) => ['Instruction', it.source, it.id, it.text]),
    ...(report.operations || []).filter((o) => !(o.affected_rows || []).length
      || o.affected_rows.some((r) => visible.has(r.index))).map((o) => ['Processing operation', o.source, o.id,
      cell({ status: o.status, reason: o.reason, fields: o.affected_fields,
        assignment_outcomes: (o.assignment_outcomes || []).filter((a) => visible.has(a.row?.index)) })]),
  ];
  return [{ name: 'Targeting assignments', rows: targeting },
    { name: 'Instruction audit', rows: instructions }, { name: 'Matching evidence', rows: evidence }];
}
